import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import Icon from '@mui/material/Icon';

import MDBox from 'components/MDBox';

import Sidenav from 'components/Sidenav';
import Configurator from 'components/Configurator';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { setMiniSidenav, setOpenConfigurator, useMaterialUIController } from 'context';
import { useIntl } from 'react-intl';
import useRoutes from 'hook/useRoutes';
import useConf from 'hook/useConf';
import { useGetConfigurationQuery } from 'services/applicationApi';
import IndeterminateLoading from 'components/Loading/IndeterminateLoading';
import { Alert, AlertTitle } from '@mui/material';
import { fErrors } from 'utils/formatError';
import { NOT_FOUND } from 'utils/messageUtils';
import * as PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import ArcadeRoutes from './ArcadeRoutes';

function MainLayout({
  layout,
  color,
  brandName,
  routes,
  onMouseEnter,
  onMouseLeave,
  configsButton
}) {
  return (
    <>
      <CssBaseline />
      {layout === 'dashboard' && (
        <Sidenav
          color={color}
          brandName={brandName}
          routes={routes}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        />
      )}

      <ArcadeRoutes routes={routes} />

      {layout === 'dashboard' && (
        <>
          <Configurator />
          {configsButton}
        </>
      )}
      {layout === 'vr' && <Configurator />}
    </>
  );
}

MainLayout.propTypes = {
  layout: PropTypes.any,
  color: PropTypes.any,
  brandName: PropTypes.string,
  routes: PropTypes.any,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  configsButton: PropTypes.any
};

export default function App() {
  const { formatMessage: msg } = useIntl();
  const { pathname } = useLocation();

  const {
    getValue: conf,
    views,
    isLoading: confLoading,
    error: confError
  } = useConf(useGetConfigurationQuery);

  const { controller, dispatch } = useMaterialUIController();
  const { miniSidenav, direction, layout, openConfigurator, sidenavColor } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);

  const routes = useRoutes(views, conf);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: 'rtl',
      stylisPlugins: [rtlPlugin]
    });

    setRtlCache(cacheRtl);
  }, []);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute('dir', direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const configsButton = useMemo(() => {
    if (conf('ui.customizable')) {
      return (
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="3.25rem"
          height="3.25rem"
          bgColor="white"
          shadow="sm"
          borderRadius="50%"
          position="fixed"
          right="2rem"
          bottom="2rem"
          zIndex={99}
          color="dark"
          sx={{ cursor: 'pointer' }}
          onClick={() => setOpenConfigurator(dispatch, !openConfigurator)}
        >
          <Icon fontSize="small" color="inherit">
            settings
          </Icon>
        </MDBox>
      );
    }
    return null;
  }, [conf, dispatch, openConfigurator]);

  if (confLoading) {
    return (
      <IndeterminateLoading sx={{ width: '100%' }} message={msg({ id: 'loading.configuration' })} />
    );
  }
  if (confError) {
    return (
      <Alert severity="error">
        <AlertTitle>{msg({ id: 'notification.error.title' })}</AlertTitle>
        {msg({ id: 'error.configuration' }, { error: fErrors(msg, confError) })}
      </Alert>
    );
  }
  return (
    <>
      <Helmet>
        <meta name="description" content={msg({ id: 'product.description' })} />
      </Helmet>
      {direction === 'rtl' ? (
        <CacheProvider value={rtlCache}>
          <MainLayout
            layout={layout}
            color={sidenavColor}
            brandName={msg({ id: 'product.logo.name', defaultMessage: NOT_FOUND })}
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
            configsButton={configsButton}
          />
        </CacheProvider>
      ) : (
        <MainLayout
          layout={layout}
          color={sidenavColor}
          brandName={msg({ id: 'product.logo.name', defaultMessage: NOT_FOUND })}
          routes={routes}
          onMouseEnter={handleOnMouseEnter}
          onMouseLeave={handleOnMouseLeave}
          configsButton={configsButton}
        />
      )}
    </>
  );
}
